import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { JobDescriptionRoleMapping, ProjectJobDescription } from "../../Models";
import ConfirmModal from "../Shared/ConfirmModal";
import { useNotification } from "../../context/NotificationContext";
import {
  DeleteJobDescription,
  SetJobDescriptionRoleActive,
  UpdateJobDescription,
} from "../../api/jobdescription.api";
import useApiToken from "../../hooks/useApiToken";
import JobDescriptionModal, { JobDescriptionPayload } from "./JobDescriptionModal";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function JobDescriptionCard({
  jobDescription,
  fetchJobDescriptions,
}: {
  jobDescription: ProjectJobDescription;
  fetchJobDescriptions: () => void;
}) {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
  const [toggleModalIsOpen, setToggleModalIsOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(jobDescription.isActive);
  const [roleMappings, setRoleMappings] = useState<JobDescriptionRoleMapping[]>(
    jobDescription.roleMappings
  );
  const { success } = useNotification();
  const token = useApiToken();

  const handleSave = async (payload: JobDescriptionPayload) => {
    if (!token) return;
    await UpdateJobDescription(jobDescription.id, payload, token);
    success("Success", "Job description has been updated!");
    fetchJobDescriptions();
  };

  const toggleOnConfirm = async () => {
    if (!token) return;
    await UpdateJobDescription(
      jobDescription.id,
      {
        title: jobDescription.title,
        content: jobDescription.content,
        isActive: !isActive,
        assigneeEmails: jobDescription.assigneeEmails ?? null,
      },
      token
    );
    setIsActive(!isActive);
    setToggleModalIsOpen(false);
    success(
      "Success",
      `Job description is now ${!isActive ? "reopened for" : "closed for"} all roles.`
    );
  };

  const toggleRoleMapping = async (roleId: string, nextIsActive: boolean) => {
    if (!token) return;
    await SetJobDescriptionRoleActive(jobDescription.id, roleId, nextIsActive, token);
    setRoleMappings((prev) =>
      prev.map((m) => (m.roleId === roleId ? { ...m, isActive: nextIsActive } : m))
    );
    success(
      "Success",
      `Job description is now ${nextIsActive ? "visible" : "hidden"} for that role.`
    );
  };

  const deleteOnConfirm = async () => {
    if (!token) return;
    await DeleteJobDescription(jobDescription.id, token);
    setDeleteModalIsOpen(false);
    success("Success", "Job description has been deleted!");
    fetchJobDescriptions();
  };

  return (
    <>
      <div className="border-b border-gray-200 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 dark:border-gray-800">
        <div className="sm:flex sm:items-baseline sm:justify-between hover:bg-gradient-to-r from-white via-gray-50 to-white pl-2 py-4 rounded-sm dark:from-darkbg dark:via-darkbglight dark:to-darkbg">
          <div className="grid grid-cols-3 sm:w-full sm:flex-1">
            <div className="col-span-2">
              <h1
                id="message-heading"
                className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-300"
              >
                <span>{jobDescription.title}</span>
              </h1>
              {jobDescription.assigneeEmails && (
                <div className="mt-1 flex items-center text-xs text-gray-400 truncate">
                  Team: {jobDescription.assigneeEmails}
                </div>
              )}
              {roleMappings.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {roleMappings.map((mapping) => (
                    <button
                      key={mapping.roleId}
                      type="button"
                      onClick={() => toggleRoleMapping(mapping.roleId, !mapping.isActive)}
                      title={
                        mapping.isActive
                          ? "Visible for this role — click to hide"
                          : "Hidden for this role — click to show"
                      }
                      className={classNames(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                        mapping.isActive
                          ? "bg-blue-50 text-blue-700 ring-blue-600/20 hover:bg-blue-100"
                          : "bg-gray-100 text-gray-400 ring-gray-400/20 hover:bg-gray-200 line-through"
                      )}
                    >
                      {mapping.roleTitle}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2 text-xs italic text-gray-400">
                  Not attached to any role
                </div>
              )}
            </div>
            <div className="flex items-center">
              {isActive ? (
                <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                  Closed
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 mb-0 flex items-center justify-between sm:ml-6 sm:mt-0 sm:flex-shrink-0 sm:justify-start">
            <Menu as="div" className="relative ml-3 inline-block text-left">
              <div>
                <Menu.Button className="-my-2 flex items-center rounded-full bg-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          className={classNames(
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700 dark:text-gray-400",
                            "flex w-full justify-between px-4 py-2 text-sm"
                          )}
                          onClick={() => setIsEditModalOpen(true)}
                        >
                          <span>Edit</span>
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          className={classNames(
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700 dark:text-gray-400",
                            "flex w-full justify-between px-4 py-2 text-sm"
                          )}
                          onClick={() => setToggleModalIsOpen(true)}
                        >
                          <span>{isActive ? "Close for all roles" : "Reopen for all roles"}</span>
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          className={classNames(
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700 dark:text-gray-400",
                            "flex w-full justify-between px-4 py-2 text-sm"
                          )}
                          onClick={() => setDeleteModalIsOpen(true)}
                        >
                          <span>Delete</span>
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <JobDescriptionModal
          initialData={jobDescription}
          onSave={handleSave}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}

      <ConfirmModal
        open={toggleModalIsOpen}
        setOpen={setToggleModalIsOpen}
        onConfirm={toggleOnConfirm}
        type="none"
        title={isActive ? "Close job description" : "Reopen job description"}
        description={`Are you sure you want to ${
          isActive ? "close" : "reopen"
        } this job description for every role it's attached to? ${
          isActive
            ? "It will disappear from every role's application flow until reopened."
            : "It will reappear for roles where it isn't individually hidden."
        }`}
      />

      <ConfirmModal
        open={deleteModalIsOpen}
        setOpen={setDeleteModalIsOpen}
        onConfirm={deleteOnConfirm}
        type="danger"
        title="Delete job description"
        description="Are you sure you want to delete this job description? All of the
        data will be permanently removed. This action cannot be undone."
      />
    </>
  );
}
